import { useState } from "react";
import { Link, useSearch } from "wouter";
import apiService from "@/services/api.service";
import FooterPublic from "@/components/layout/footer-public";

const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

export default function AceitarConvite() {
  const searchString = useSearch();
  const params = new URLSearchParams(searchString);
  const token = params.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passwordValid = PASSWORD_REGEX.test(password);
  const passwordsMatch = password === confirmPassword;
  const canSubmit = passwordValid && passwordsMatch && !!token;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setIsLoading(true);
    setError(null);

    const result = await apiService.post(
      "/company-invites/accept",
      { token, password },
      { skipAuth: true },
    );

    setIsLoading(false);

    if (result.ok) {
      setSuccess(true);
    } else {
      setError(result.error || "Erro ao aceitar convite. O link pode ter expirado.");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-4">
        <div className="text-center max-w-sm">
          <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-neutral-900 mb-2" data-testid="invalid-link">Link inválido</h1>
          <p className="text-neutral-500 mb-6">Este link de convite é inválido ou está incompleto.</p>
          <Link href="/login" className="text-sm font-medium text-blue-600 hover:text-blue-800">
            Voltar para o login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* Left Side */}
      <div className="w-full lg:w-1/2 flex flex-col px-4 sm:px-8 md:px-16 lg:px-24 py-8 sm:py-12 lg:py-0 bg-white min-h-screen lg:min-h-0">
        <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex justify-start mb-6">
            <img
              src="/assets/login/image-logo.svg"
              alt="Liqi - Portal do cliente"
              className="h-12 sm:h-14 w-auto"
            />
          </div>

          {success ? (
            <>
              {/* Success State */}
              <div className="mb-8">
                <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                  </svg>
                </div>
                <h1 style={{ fontSize: "30px", lineHeight: "36px" }} className="font-semibold text-neutral-900 mb-2" data-testid="accept-success">
                  Conta criada com sucesso!
                </h1>
                <p style={{ fontSize: "16px", lineHeight: "24px" }} className="text-neutral-500">
                  Sua conta foi criada. Você já pode acessar o portal com seu email e senha.
                </p>
              </div>

              <Link
                href="/login"
                className="inline-flex w-full justify-center py-3 px-6 rounded-lg text-white font-semibold text-base transition-all"
                style={{ backgroundColor: "#0247FE" }}
              >
                Ir para o login
              </Link>
            </>
          ) : (
            <>
              {/* Form State */}
              <div className="mb-8">
                <h1 style={{ fontSize: "30px", lineHeight: "36px" }} className="font-semibold text-neutral-900 mb-2">
                  Aceitar convite
                </h1>
                <p style={{ fontSize: "16px", lineHeight: "24px" }} className="text-neutral-500">
                  Crie uma senha para sua conta. A senha deve ter no mínimo 8 caracteres, 1 letra maiúscula, 1 número e 1 caractere especial.
                </p>
              </div>

              {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-sm text-red-700" data-testid="accept-error">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                {/* Senha */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="password" className="text-sm font-medium text-neutral-900">
                    Senha
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Mín 8 caracteres, 1 maiúscula, 1 número, 1 especial"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full py-3 px-4 pr-12 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300 focus:border-transparent"
                      style={{ backgroundColor: "#ffffff", color: "#171717" }}
                      required
                      disabled={isLoading}
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-neutral-100 rounded transition-colors"
                    >
                      <svg className="w-5 h-5 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        {showPassword ? (
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12c1.292 4.338 5.31 7.5 10.066 7.5.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                        )}
                      </svg>
                    </button>
                  </div>
                  {password && !passwordValid && (
                    <p className="text-xs text-red-500" data-testid="password-validation">
                      A senha deve ter no mínimo 8 caracteres, 1 maiúscula, 1 número e 1 caractere especial
                    </p>
                  )}
                </div>

                {/* Confirmar senha */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="confirmPassword" className="text-sm font-medium text-neutral-900">
                    Confirmar senha
                  </label>
                  <input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="Repita a senha"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full py-3 px-4 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300 focus:border-transparent"
                    style={{ backgroundColor: "#ffffff", color: "#171717" }}
                    required
                    disabled={isLoading}
                  />
                  {confirmPassword && !passwordsMatch && (
                    <p className="text-xs text-red-500" data-testid="password-mismatch">As senhas não coincidem</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !canSubmit}
                  className="w-full py-3 px-6 rounded-lg text-white font-semibold text-base transition-all disabled:opacity-50"
                  style={{ backgroundColor: "#0247FE" }}
                  data-testid="accept-submit"
                >
                  {isLoading ? "Criando conta..." : "Criar conta"}
                </button>
              </form>

              <div className="mt-6 text-center">
                <Link
                  href="/login"
                  className="inline-flex items-center gap-2 text-sm font-medium text-neutral-500 hover:text-neutral-700 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
                  </svg>
                  Voltar para o login
                </Link>
              </div>
            </>
          )}
        </div>
        </div>
        <FooterPublic />
      </div>

      {/* Right Side - Branding Panel (dark) */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center p-8 lg:p-12 relative overflow-hidden" style={{ backgroundColor: "#111218" }}>
        <div className="relative z-10 max-w-2xl mx-auto w-full flex flex-col justify-between min-h-[60vh]">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-12">
              <img src="/assets/login/login-logo-white.svg" alt="Liqi" className="h-8 lg:h-10 w-auto" />
            </div>
            <div className="mb-12">
              <h2 style={{ fontSize: "36px", lineHeight: "44px" }} className="font-semibold text-white mb-4">
                Governança completa para
                <br />
                suas operações
              </h2>
              <p style={{ fontSize: "16px", lineHeight: "28px" }} className="text-white/60 max-w-md mx-auto">
                Gerencie vencimentos, pagamentos, projeções
                de caixa e análises com inteligência artificial.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
